import {
  ComponentPropsWithRef,
  ElementType,
  ReactNode,
  createElement,
} from 'react';

const defaultElement = 'span';
const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl'] as const;
const defaultToken = 'sans/16/regular';

type TextBreakpoint = (typeof breakpoints)[number];
type TextFamily = 'sans' | 'display' | 'mono';
type TextWeight = 'regular' | 'medium' | 'bold';

export type TextLeading =
  | 'body'
  | 'compact'
  | 'compactEditorial'
  | 'editorial'
  | 'headline'
  | 'title';

interface TextRecipe {
  className: string;
}

// Keep runtime-generated classes in sync with `packages/ui/theme/config.css`.
// Any new arbitrary `text-*`, `tracking-*`, `leading-*`, or responsive token
// classes added here must be safelisted there so Tailwind packages them.
const leadingClassNames: Record<TextLeading, string> = {
  body: 'leading-[1.25]',
  compact: 'leading-[1.2]',
  compactEditorial: 'leading-[calc(1em+4px)]',
  editorial: 'leading-[calc(1em+8px)]',
  headline: 'leading-[1.125]',
  title: 'leading-none',
};

const textTokens = {
  'sans/12/regular': {
    className: 'font-sans text-[12px] font-normal',
  },
  'sans/12/medium': {
    className: 'font-sans text-[12px] font-medium',
  },
  'sans/12/bold': {
    className: 'font-sans text-[12px] font-bold',
  },
  'sans/14/regular': {
    className: 'font-sans text-[14px] font-normal',
  },
  'sans/14/medium': {
    className: 'font-sans text-[14px] font-medium',
  },
  'sans/14/bold': {
    className: 'font-sans text-[14px] font-bold',
  },
  'sans/16/regular': {
    className: 'font-sans text-[16px] font-normal',
  },
  'sans/16/medium': {
    className: 'font-sans text-[16px] font-medium',
  },
  'sans/16/bold': {
    className: 'font-sans text-[16px] font-bold',
  },
  'sans/20/regular': {
    className: 'font-sans text-[20px] font-normal',
  },
  'sans/20/medium': {
    className: 'font-sans text-[20px] font-medium',
  },
  'sans/22/medium': {
    className: 'font-sans text-[22px] font-medium',
  },
  'sans/24/medium': {
    className: 'font-sans text-[24px] font-medium',
  },
  'sans/24/regular': {
    className: 'font-sans text-[24px] font-normal',
  },
  'sans/28/regular': {
    className: 'font-sans text-[28px] font-normal',
  },
  'sans/28/medium': {
    className: 'font-sans text-[28px] font-medium',
  },
  'sans/32/regular': {
    className: 'font-sans text-[32px] font-normal',
  },
  'sans/32/medium': {
    className: 'font-sans text-[32px] font-medium',
  },
  'sans/36/medium': {
    className: 'font-sans text-[36px] font-medium',
  },
  'display/14/regular': {
    className: 'font-display text-[14px] font-normal tracking-tighter',
  },
  'display/14/medium': {
    className: 'font-display text-[14px] font-medium tracking-tighter',
  },
  'display/16/regular': {
    className: 'font-display text-[16px] font-normal tracking-tighter',
  },
  'display/16/medium': {
    className: 'font-display text-[16px] font-medium tracking-tighter',
  },
  'display/20/medium': {
    className: 'font-display text-[20px] font-medium tracking-tighter',
  },
  'display/20/bold': {
    className: 'font-display text-[20px] font-bold tracking-tighter',
  },
  'display/24/bold': {
    className: 'font-display text-[24px] font-bold tracking-tighter',
  },
  'display/32/bold': {
    className: 'font-display text-[32px] font-bold tracking-tighter',
  },
  'display/34/bold': {
    className: 'font-display text-[34px] font-bold tracking-tighter',
  },
  'display/36/bold': {
    className: 'font-display text-[36px] font-bold tracking-tighter',
  },
  'display/40/bold': {
    className: 'font-display text-[40px] font-bold tracking-tighter',
  },
  'display/56/bold': {
    className: 'font-display text-[56px] font-bold tracking-tighter',
  },
  'display/64/bold': {
    className: 'font-display text-[64px] font-bold tracking-tighter',
  },
  'display/72/bold': {
    className: 'font-display text-[72px] font-bold tracking-tighter',
  },
  'display/88/bold': {
    className: 'font-display text-[88px] font-bold tracking-tighter',
  },
} as const satisfies Record<string, TextRecipe>;

type CanonicalTextToken = keyof typeof textTokens;

type PublicTextToken<T extends CanonicalTextToken = CanonicalTextToken> =
  T extends `${infer Family}/${infer Size}/${infer Weight}`
    ?
        | T
        | (Family extends 'sans'
            ? Weight extends 'regular'
              ? `${Size}`
              : `${Size}/${Weight}`
            : Weight extends 'regular'
              ? `${Family}/${Size}`
              : `${Family}/${Size}/${Weight}`)
    : never;

export type TextToken = PublicTextToken;

export interface ResponsiveTextToken {
  base: TextToken;
  sm?: TextToken;
  md?: TextToken;
  lg?: TextToken;
  xl?: TextToken;
  '2xl'?: TextToken;
}

export interface ResponsiveTextLeading {
  base: TextLeading;
  sm?: TextLeading;
  md?: TextLeading;
  lg?: TextLeading;
  xl?: TextLeading;
  '2xl'?: TextLeading;
}

interface TextBaseProps {
  token: TextToken | ResponsiveTextToken;
  leading?: TextLeading | ResponsiveTextLeading;
  className?: string;
  children?: ReactNode;
}

export type TextProps<C extends ElementType = typeof defaultElement> =
  TextBaseProps & {
    as?: C;
  } & Omit<ComponentPropsWithRef<C>, keyof TextBaseProps>;

function isTextFamily(value: string): value is TextFamily {
  return value === 'sans' || value === 'display' || value === 'mono';
}

function isTextWeight(value: string): value is TextWeight {
  return value === 'regular' || value === 'medium' || value === 'bold';
}

// Accept shorthand tokens like `14`, `14/medium`, and `display/16`.
function normalizeTextToken(token: TextToken): CanonicalTextToken {
  const segments = token.split('/');
  let canonicalToken: string;

  if (segments.length === 1) {
    canonicalToken = `sans/${segments[0]}/regular`;
  } else if (segments.length === 2) {
    canonicalToken = isTextFamily(segments[0])
      ? `${segments[0]}/${segments[1]}/regular`
      : `sans/${segments[0]}/${segments[1]}`;
  } else if (segments.length === 3) {
    canonicalToken = token;
  } else {
    return defaultToken;
  }

  const canonicalTokenValue = canonicalToken as CanonicalTextToken;

  if (canonicalTokenValue in textTokens) {
    return canonicalTokenValue;
  }

  const inferredWeight = segments[segments.length - 1];

  if (inferredWeight && !isTextWeight(inferredWeight)) {
    return defaultToken;
  }

  return defaultToken;
}

function prefixTokenClasses(className: string, breakpoint: TextBreakpoint) {
  return className
    .split(' ')
    .filter(Boolean)
    .map((tokenClassName) => `${breakpoint}:${tokenClassName}`)
    .join(' ');
}

function getDefaultLeading(token: CanonicalTextToken): TextLeading {
  const [family, sizeString] = token.split('/');
  const size = Number(sizeString);

  if (family === 'display' || size >= 22) {
    return 'headline';
  }

  return 'body';
}

function getTokenClassName(token: TextToken, leading?: TextLeading) {
  const canonicalToken = normalizeTextToken(token);
  const recipe = textTokens[canonicalToken];
  const resolvedLeading = leading ?? getDefaultLeading(canonicalToken);

  return `${recipe.className} ${leadingClassNames[resolvedLeading]}`;
}

function getTextClassName(
  token: TextToken | ResponsiveTextToken,
  leading?: TextLeading | ResponsiveTextLeading,
  className?: string,
) {
  if (typeof token === 'string') {
    const baseLeading = typeof leading === 'string' ? leading : leading?.base;
    const responsiveLeading = typeof leading === 'string' ? undefined : leading;

    return [
      getTokenClassName(token, baseLeading),
      ...breakpoints.flatMap((breakpoint) => {
        const breakpointLeading = responsiveLeading?.[breakpoint];
        return breakpointLeading
          ? prefixTokenClasses(leadingClassNames[breakpointLeading], breakpoint)
          : [];
      }),
      className,
    ]
      .filter(Boolean)
      .join(' ');
  }

  const baseLeading = typeof leading === 'string' ? leading : leading?.base;
  const responsiveLeading = typeof leading === 'string' ? undefined : leading;

  return [
    getTokenClassName(token.base, baseLeading),
    ...breakpoints.flatMap((breakpoint) => {
      const breakpointToken = token[breakpoint];
      const breakpointLeading = responsiveLeading?.[breakpoint] ?? baseLeading;
      const tokenClasses = breakpointToken
        ? prefixTokenClasses(
            getTokenClassName(breakpointToken, breakpointLeading),
            breakpoint,
          )
        : breakpointLeading && breakpointLeading !== baseLeading
          ? prefixTokenClasses(leadingClassNames[breakpointLeading], breakpoint)
          : '';

      return tokenClasses ? [tokenClasses] : [];
    }),
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export default function Text<C extends ElementType = typeof defaultElement>({
  as,
  children,
  token,
  leading,
  className,
  ...restProps
}: TextProps<C>) {
  const Component = as || defaultElement;
  const classNameValue = getTextClassName(token, leading, className);

  return createElement(
    Component,
    {
      className: classNameValue,
      ...restProps,
    },
    children,
  );
}
